import { useEffect, useRef, useState, type Dispatch, type MouseEvent as ReactMouseEvent, type RefObject, type SetStateAction } from "react"

import type { ReportLayoutWidgetDto, ReportTemplateFlowSectionDto } from "../../api/reportTemplateService"
import {
  clamp,
  getWidgetFlowSectionId,
  getWidgetLocked,
  getWidgetManualPage,
  getWidgetPositionMode,
  MIN_FLOW_SECTION_HEIGHT,
  MIN_WIDGET_HEIGHT,
  MIN_WIDGET_WIDTH,
  snapToGridValue,
  toFiniteNumber,
  type ResizeMode,
} from "./editorUtils"
import type { LayoutSectionKey } from "./layoutTypes"

type UseLayoutWidgetInteractionsParams = {
  canvasRef: RefObject<HTMLDivElement | null>
  editCanvasMetrics: { width: number; height: number }
  layoutViewMode: "design" | "preview"
  layoutSection: LayoutSectionKey
  layoutWidgets: ReportLayoutWidgetDto[]
  designBodyPage: number
  manualPageCount: number
  primaryFlowSectionId: string
  flowSections: ReportTemplateFlowSectionDto[]
  designFlowSectionBoxes: Array<{ sectionId: string; y: number; height: number }>
  snapToGrid: boolean
  gridSize: number
  setSectionWidgets: (section: LayoutSectionKey, updater: (widgets: ReportLayoutWidgetDto[]) => ReportLayoutWidgetDto[]) => void
  setSelectedWidgetId: Dispatch<SetStateAction<string | null>>
  updateFlowSection: (sectionId: string, patch: Partial<ReportTemplateFlowSectionDto>) => void
}

export function useLayoutWidgetInteractions({
  canvasRef,
  editCanvasMetrics,
  layoutViewMode,
  layoutSection,
  layoutWidgets,
  designBodyPage,
  manualPageCount,
  primaryFlowSectionId,
  flowSections,
  designFlowSectionBoxes,
  snapToGrid,
  gridSize,
  setSectionWidgets,
  setSelectedWidgetId,
  updateFlowSection,
}: UseLayoutWidgetInteractionsParams) {
  const [movingWidgetId, setMovingWidgetId] = useState<string | null>(null)
  const [movingSectionId, setMovingSectionId] = useState<string | null>(null)
  const [resizingSectionId, setResizingSectionId] = useState<string | null>(null)
  const [isMoveTracking, setIsMoveTracking] = useState(false)
  const [isSectionMoveTracking, setIsSectionMoveTracking] = useState(false)
  const [isSectionResizeTracking, setIsSectionResizeTracking] = useState(false)
  const [resizingWidgetId, setResizingWidgetId] = useState<string | null>(null)

  const moveSessionRef = useRef<{
    widgetId: string
    startClientX: number
    startClientY: number
    startX: number
    startY: number
    startFlowSectionId: string
    startAbsoluteY: number
    positionMode: "flow" | "absolute"
    sectionBoxesSnapshot: Array<{ sectionId: string; y: number; height: number }>
    scaleX: number
    scaleY: number
    dragStarted: boolean
  } | null>(null)
  const sectionMoveSessionRef = useRef<{
    sectionId: string
    widgetIds: string[]
    startClientX: number
    startClientY: number
    minDx: number
    maxDx: number
    minDy: number
    maxDy: number
    scaleX: number
    scaleY: number
    dragStarted: boolean
    startPositions: Record<string, { x: number; y: number }>
  } | null>(null)
  const sectionResizeSessionRef = useRef<{
    sectionId: string
    startClientY: number
    startMinHeight: number
    scaleY: number
  } | null>(null)
  const resizeSessionRef = useRef<{
    widgetId: string
    startClientX: number
    startClientY: number
    startWidth: number
    startHeight: number
    scaleX: number
    scaleY: number
    mode: ResizeMode
  } | null>(null)

  useEffect(() => {
    if (!isMoveTracking) return

    const handleMouseMove = (event: MouseEvent) => {
      const session = moveSessionRef.current
      if (!session) return

      const clientDeltaX = event.clientX - session.startClientX
      const clientDeltaY = event.clientY - session.startClientY
      if (!session.dragStarted) {
        const dragThreshold = 8
        if (Math.abs(clientDeltaX) < dragThreshold && Math.abs(clientDeltaY) < dragThreshold) {
          return
        }
        session.dragStarted = true
        setMovingWidgetId(session.widgetId)
      }

      const deltaX = clientDeltaX * session.scaleX
      const deltaY = clientDeltaY * session.scaleY

      setSectionWidgets(layoutSection, (widgets) =>
        widgets.map((widget) => {
          if (widget.id !== session.widgetId) return widget
          if (getWidgetLocked(widget)) return widget
          const safeWidth = toFiniteNumber(widget.width, 240)
          const safeHeight = toFiniteNumber(widget.height, 56)
          const maxX = Math.max(0, editCanvasMetrics.width - safeWidth)
          const nextX = clamp(session.startX + deltaX, 0, maxX)
          const snappedX = clamp(snapToGridValue(nextX, gridSize, snapToGrid), 0, maxX)

          if (layoutSection !== "body" || session.positionMode === "absolute") {
            const maxY = Math.max(0, editCanvasMetrics.height - safeHeight)
            const nextY = clamp(session.startY + deltaY, 0, maxY)
            const snappedY = clamp(snapToGridValue(nextY, gridSize, snapToGrid), 0, maxY)
            return {
              ...widget,
              x: snappedX,
              y: snappedY,
            }
          }

          const absoluteYRaw = session.startAbsoluteY + deltaY
          const absoluteCenterY = absoluteYRaw + safeHeight / 2
          const orderedBoxes = [...session.sectionBoxesSnapshot].sort((a, b) => a.y - b.y)
          let targetBox =
            orderedBoxes.find((box) => absoluteCenterY >= box.y && absoluteCenterY <= box.y + box.height) ?? null
          if (!targetBox && orderedBoxes.length > 0) {
            targetBox = absoluteCenterY < orderedBoxes[0].y ? orderedBoxes[0] : orderedBoxes[orderedBoxes.length - 1]
          }
          const fallbackBox =
            targetBox ??
            orderedBoxes.find((box) => box.sectionId === session.startFlowSectionId) ??
            null
          const localYRaw = fallbackBox ? absoluteYRaw - fallbackBox.y : session.startY + deltaY
          const localYMax = Math.max(0, (fallbackBox?.height ?? safeHeight) - safeHeight)
          const localYSnapped = clamp(
            snapToGridValue(localYRaw, gridSize, snapToGrid),
            0,
            localYMax,
          )

          return {
            ...widget,
            x: snappedX,
            y: localYSnapped,
            props: {
              ...(widget.props && typeof widget.props === "object" ? widget.props : {}),
              flowSectionId: fallbackBox?.sectionId || session.startFlowSectionId || primaryFlowSectionId,
            },
          }
        }),
      )
    }

    const handleMouseUp = () => {
      setMovingWidgetId(null)
      setIsMoveTracking(false)
      moveSessionRef.current = null
    }

    window.addEventListener("mousemove", handleMouseMove)
    window.addEventListener("mouseup", handleMouseUp)

    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
    }
  }, [editCanvasMetrics.height, editCanvasMetrics.width, gridSize, isMoveTracking, layoutSection, primaryFlowSectionId, setSectionWidgets, snapToGrid])

  useEffect(() => {
    if (!isSectionMoveTracking) return

    const handleMouseMove = (event: MouseEvent) => {
      const session = sectionMoveSessionRef.current
      if (!session) return

      const clientDeltaX = event.clientX - session.startClientX
      const clientDeltaY = event.clientY - session.startClientY
      if (!session.dragStarted) {
        const dragThreshold = 8
        if (Math.abs(clientDeltaX) < dragThreshold && Math.abs(clientDeltaY) < dragThreshold) {
          return
        }
        session.dragStarted = true
        setMovingSectionId(session.sectionId)
      }

      const rawDx = clientDeltaX * session.scaleX
      const rawDy = clientDeltaY * session.scaleY
      const dx = clamp(rawDx, session.minDx, session.maxDx)
      const dy = clamp(rawDy, session.minDy, session.maxDy)

      setSectionWidgets("body", (widgets) =>
        widgets.map((widget) => {
          if (!session.widgetIds.includes(widget.id)) return widget
          if (getWidgetLocked(widget)) return widget
          const start = session.startPositions[widget.id]
          if (!start) return widget
          const nextX = start.x + dx
          const nextY = start.y + dy
          const safeWidth = Math.max(1, toFiniteNumber(widget.width, 1))
          const safeHeight = Math.max(1, toFiniteNumber(widget.height, 1))
          const maxX = Math.max(0, editCanvasMetrics.width - safeWidth)
          const maxY =
            layoutSection === "body"
              ? Number.POSITIVE_INFINITY
              : Math.max(0, editCanvasMetrics.height - safeHeight)
          return {
            ...widget,
            x: clamp(snapToGridValue(nextX, gridSize, snapToGrid), 0, maxX),
            y: clamp(snapToGridValue(nextY, gridSize, snapToGrid), 0, maxY),
          }
        }),
      )
    }

    const handleMouseUp = () => {
      setMovingSectionId(null)
      setIsSectionMoveTracking(false)
      sectionMoveSessionRef.current = null
    }

    window.addEventListener("mousemove", handleMouseMove)
    window.addEventListener("mouseup", handleMouseUp)

    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
    }
  }, [editCanvasMetrics.height, editCanvasMetrics.width, gridSize, isSectionMoveTracking, layoutSection, setSectionWidgets, snapToGrid])

  useEffect(() => {
    if (!isSectionResizeTracking) return

    const handleMouseMove = (event: MouseEvent) => {
      const session = sectionResizeSessionRef.current
      if (!session) return

      const clientDeltaY = event.clientY - session.startClientY
      const deltaY = clientDeltaY * session.scaleY
      const nextHeight = clamp(Math.round(session.startMinHeight + deltaY), MIN_FLOW_SECTION_HEIGHT, 2400)

      updateFlowSection(session.sectionId, { minHeight: nextHeight })
      setResizingSectionId(session.sectionId)
    }

    const handleMouseUp = () => {
      setResizingSectionId(null)
      setIsSectionResizeTracking(false)
      sectionResizeSessionRef.current = null
    }

    window.addEventListener("mousemove", handleMouseMove)
    window.addEventListener("mouseup", handleMouseUp)

    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
    }
  }, [isSectionResizeTracking])

  useEffect(() => {
    if (!resizingWidgetId) return

    const handleMouseMove = (event: MouseEvent) => {
      const session = resizeSessionRef.current
      if (!session) return

      const deltaX = (event.clientX - session.startClientX) * session.scaleX
      const deltaY = (event.clientY - session.startClientY) * session.scaleY

      const width =
        session.mode === "height"
          ? session.startWidth
          : clamp(session.startWidth + deltaX, MIN_WIDGET_WIDTH, editCanvasMetrics.width)
      const height =
        session.mode === "width"
          ? session.startHeight
          : layoutSection === "body"
            ? Math.max(MIN_WIDGET_HEIGHT, Math.round(session.startHeight + deltaY))
            : clamp(session.startHeight + deltaY, MIN_WIDGET_HEIGHT, editCanvasMetrics.height)

      setSectionWidgets(layoutSection, (widgets) =>
        widgets.map((widget) => {
          if (widget.id !== session.widgetId) return widget
          if (getWidgetLocked(widget)) return widget
          const currentX = toFiniteNumber(widget.x, 0)
          const currentY = toFiniteNumber(widget.y, 0)
          const maxX = Math.max(0, editCanvasMetrics.width - width)
          const maxY =
            layoutSection === "body"
              ? Number.POSITIVE_INFINITY
              : Math.max(0, editCanvasMetrics.height - height)
          const nextX = clamp(currentX, 0, maxX)
          const nextY = clamp(currentY, 0, maxY)
          const snappedWidth = clamp(
            snapToGridValue(width, gridSize, snapToGrid),
            MIN_WIDGET_WIDTH,
            editCanvasMetrics.width,
          )
          const snappedHeight =
            layoutSection === "body"
              ? Math.max(MIN_WIDGET_HEIGHT, Math.round(snapToGridValue(height, gridSize, snapToGrid)))
              : clamp(snapToGridValue(height, gridSize, snapToGrid), MIN_WIDGET_HEIGHT, editCanvasMetrics.height)
          const snappedMaxX = Math.max(0, editCanvasMetrics.width - snappedWidth)
          const snappedMaxY =
            layoutSection === "body"
              ? Number.POSITIVE_INFINITY
              : Math.max(0, editCanvasMetrics.height - snappedHeight)
          return {
            ...widget,
            width: snappedWidth,
            height: snappedHeight,
            x: clamp(snapToGridValue(nextX, gridSize, snapToGrid), 0, snappedMaxX),
            y: clamp(snapToGridValue(nextY, gridSize, snapToGrid), 0, snappedMaxY),
          }
        }),
      )
    }

    const handleMouseUp = () => {
      setResizingWidgetId(null)
      resizeSessionRef.current = null
    }

    window.addEventListener("mousemove", handleMouseMove)
    window.addEventListener("mouseup", handleMouseUp)

    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mouseup", handleMouseUp)
    }
  }, [editCanvasMetrics.height, editCanvasMetrics.width, gridSize, layoutSection, resizingWidgetId, setSectionWidgets, snapToGrid])

  useEffect(() => {
    setMovingWidgetId(null)
    setMovingSectionId(null)
    setResizingSectionId(null)
    setResizingWidgetId(null)
    setIsMoveTracking(false)
    setIsSectionMoveTracking(false)
    setIsSectionResizeTracking(false)
    moveSessionRef.current = null
    sectionMoveSessionRef.current = null
    sectionResizeSessionRef.current = null
    resizeSessionRef.current = null
  }, [layoutViewMode])

  useEffect(() => {
    setSelectedWidgetId(null)
    setMovingSectionId(null)
    setResizingSectionId(null)
    setIsSectionMoveTracking(false)
    setIsSectionResizeTracking(false)
    sectionMoveSessionRef.current = null
    sectionResizeSessionRef.current = null
  }, [layoutSection, setSelectedWidgetId])

  const handleResizeMouseDown = (
    widget: ReportLayoutWidgetDto,
    event: ReactMouseEvent<HTMLDivElement>,
    mode: ResizeMode = "both",
  ) => {
    if (getWidgetLocked(widget)) return
    event.preventDefault()
    event.stopPropagation()
    const canvasRect = canvasRef.current?.getBoundingClientRect()
    if (!canvasRect || canvasRect.width <= 0 || canvasRect.height <= 0) return

    setSelectedWidgetId(widget.id)
    setResizingWidgetId(widget.id)
    resizeSessionRef.current = {
      widgetId: widget.id,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startWidth: toFiniteNumber(widget.width, 240),
      startHeight: toFiniteNumber(widget.height, 56),
      scaleX: editCanvasMetrics.width / canvasRect.width,
      scaleY: editCanvasMetrics.height / canvasRect.height,
      mode,
    }
  }

  const handleWidgetMouseDown = (widget: ReportLayoutWidgetDto, event: ReactMouseEvent<HTMLDivElement>) => {
    if (getWidgetLocked(widget)) return
    if (event.button !== 0) return
    const target = event.target as HTMLElement
    if (target.closest('[data-resize-handle="true"]')) return
    event.preventDefault()
    event.stopPropagation()
    const canvasRect = canvasRef.current?.getBoundingClientRect()
    if (!canvasRect || canvasRect.width <= 0 || canvasRect.height <= 0) return

    setSelectedWidgetId(widget.id)
    setMovingWidgetId(null)
    setIsMoveTracking(true)
    const positionMode = getWidgetPositionMode(widget)
    const startFlowSectionId = getWidgetFlowSectionId(widget, primaryFlowSectionId)
    const sectionBoxesSnapshot = [...designFlowSectionBoxes].map((box) => ({
      sectionId: box.sectionId,
      y: box.y,
      height: box.height,
    }))
    const fallbackStartSectionY =
      sectionBoxesSnapshot.find((box) => box.sectionId === startFlowSectionId)?.y ?? 0
    const startSectionY =
      layoutSection === "body"
        ? fallbackStartSectionY
        : 0
    moveSessionRef.current = {
      widgetId: widget.id,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: toFiniteNumber(widget.x, 0),
      startY: toFiniteNumber(widget.y, 0),
      startFlowSectionId,
      positionMode,
      sectionBoxesSnapshot,
      startAbsoluteY:
        layoutSection === "body"
          ? startSectionY + toFiniteNumber(widget.y, 0)
          : toFiniteNumber(widget.y, 0),
      scaleX: editCanvasMetrics.width / canvasRect.width,
      scaleY: editCanvasMetrics.height / canvasRect.height,
      dragStarted: false,
    }
  }

  const handleFlowSectionMouseDown = (sectionId: string, event: ReactMouseEvent<HTMLDivElement>) => {
    if (layoutSection !== "body") return
    if (event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()

    const canvasRect = canvasRef.current?.getBoundingClientRect()
    if (!canvasRect || canvasRect.width <= 0 || canvasRect.height <= 0) return

    const memberWidgets = layoutWidgets.filter((widget) => {
      if (getWidgetLocked(widget)) return false
      const widgetPage = clamp(getWidgetManualPage(widget, 1), 1, manualPageCount)
      if (widgetPage !== designBodyPage) return false
      return getWidgetFlowSectionId(widget, primaryFlowSectionId) === sectionId
    })
    if (memberWidgets.length === 0) return

    let minDx = Number.NEGATIVE_INFINITY
    let maxDx = Number.POSITIVE_INFINITY
    let minDy = Number.NEGATIVE_INFINITY
    let maxDy = Number.POSITIVE_INFINITY
    const startPositions: Record<string, { x: number; y: number }> = {}
    const widgetIds = memberWidgets.map((widget) => widget.id)

    memberWidgets.forEach((widget) => {
      const startX = toFiniteNumber(widget.x, 0)
      const startY = toFiniteNumber(widget.y, 0)
      const width = Math.max(1, toFiniteNumber(widget.width, 1))
      const height = Math.max(1, toFiniteNumber(widget.height, 1))
      startPositions[widget.id] = { x: startX, y: startY }
      minDx = Math.max(minDx, -startX)
      maxDx = Math.min(maxDx, editCanvasMetrics.width - width - startX)
      minDy = Math.max(minDy, -startY)
      maxDy = Number.POSITIVE_INFINITY
    })

    setSelectedWidgetId(null)
    setMovingWidgetId(null)
    setIsMoveTracking(false)
    moveSessionRef.current = null
    setIsSectionMoveTracking(true)
    setMovingSectionId(null)
    sectionMoveSessionRef.current = {
      sectionId,
      widgetIds,
      startClientX: event.clientX,
      startClientY: event.clientY,
      minDx: Number.isFinite(minDx) ? minDx : 0,
      maxDx: Number.isFinite(maxDx) ? maxDx : 0,
      minDy: Number.isFinite(minDy) ? minDy : 0,
      maxDy: Number.isFinite(maxDy) ? maxDy : Number.POSITIVE_INFINITY,
      scaleX: editCanvasMetrics.width / canvasRect.width,
      scaleY: editCanvasMetrics.height / canvasRect.height,
      dragStarted: false,
      startPositions,
    }
  }

  const handleFlowSectionResizeMouseDown = (sectionId: string, event: ReactMouseEvent<HTMLDivElement>) => {
    if (layoutSection !== "body") return
    if (event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()

    const canvasRect = canvasRef.current?.getBoundingClientRect()
    if (!canvasRect || canvasRect.width <= 0 || canvasRect.height <= 0) return

    const section = flowSections.find((item) => item.sectionId === sectionId)
    if (!section) return

    setIsMoveTracking(false)
    setIsSectionMoveTracking(false)
    moveSessionRef.current = null
    sectionMoveSessionRef.current = null
    setMovingWidgetId(null)
    setMovingSectionId(null)

    setIsSectionResizeTracking(true)
    setResizingSectionId(sectionId)
    sectionResizeSessionRef.current = {
      sectionId,
      startClientY: event.clientY,
      startMinHeight: clamp(Math.floor(Number(section.minHeight) || 140), MIN_FLOW_SECTION_HEIGHT, 2400),
      scaleY: editCanvasMetrics.height / canvasRect.height,
    }
  }

  return {
    movingWidgetId,
    movingSectionId,
    resizingSectionId,
    resizingWidgetId,
    setMovingWidgetId,
    handleResizeMouseDown,
    handleWidgetMouseDown,
    handleFlowSectionMouseDown,
    handleFlowSectionResizeMouseDown,
  }
}
